/**
 * Ids for everything the assessment and assignment editors mint — definitions, screens, activities,
 * choices, rubrics, and a math homework's exercises and their list items.
 *
 * SHARED because of B7: a map keyed by the bare numeric strings `"0"`, `"1"`, … is indistinguishable
 * from a list once the document has passed through a PHP array, so it round-trips as a JSON ARRAY and
 * the keys are lost. Rubric ids are the keys of `data.rubrics`, so a bare counter there silently
 * destroys the catalog on the next save. Real data already prefixes its ids, which is only
 * enforceable if there is exactly one place that mints them.
 *
 * There is no `uuid`/`nanoid` dependency in this repo and none may be added, so this is
 * `crypto.randomUUID` with a fallback for non-secure contexts; its output is non-numeric too.
 */

/** The prefix each kind of id carries. Screens, activities and choices are not map keys, but
 *  they are prefixed anyway so an id is self-describing in a stack trace or a diff. */
export const ID_PREFIX = {
  assessment: 'asmt',
  screen: 'screen',
  activity: 'activity',
  choice: 'choice',
  rubric: 'rubric',
  exercise: 'exercise',
  pair: 'pair',
  item: 'item',
  tick: 'tick',
  step: 'step',
} as const

export type IdPrefix = typeof ID_PREFIX[keyof typeof ID_PREFIX]

/** `mintId('screen')` → `screen-9f1c…`. Never numeric, never a bare counter. */
export const mintId = (prefix: IdPrefix): string => {
  const uuid = globalThis.crypto?.randomUUID?.()
  const suffix = uuid || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

  return `${prefix}-${suffix}`
}
